import {AssetType} from '../Assets.enums';
import {CLMimeType} from '../../media/media.types';
import {
  imageMediaMimeTypes,
  videoMediaMimeTypes,
  fontMediaMimeTypes,
  subtitlesMediaMimeTypes,
} from '../../media/media.consts';

// Animated image mime types (subset of image types that can be animated)
const animatedImageMimeTypes = [CLMimeType.GIF, CLMimeType.APNG, CLMimeType.WEBP];

// Audio mime types
const audioMimeTypes = [
  CLMimeType.MP3,
  CLMimeType.WAV,
  CLMimeType.M4A,
  CLMimeType.OGG,
  CLMimeType.FLACC,
];

// Document mime types
const documentMimeTypes = [
  CLMimeType.PDF,
  CLMimeType.CSV,
  CLMimeType.XLS,
  CLMimeType.XLSX,
  CLMimeType.TSV,
];

// Code mime types
const codeMimeTypes = [CLMimeType.XML, CLMimeType.TEXT_XML];

// Extension to AssetType mapping for types not easily identified by mime type
const extensionToAssetTypeMap: Record<string, AssetType> = {
  // Images
  jpg: AssetType.Image,
  jpeg: AssetType.Image,
  png: AssetType.Image,
  svg: AssetType.Image,
  webp: AssetType.Image,
  // Animated images
  gif: AssetType.AnimatedImage,
  apng: AssetType.AnimatedImage,
  // Videos
  mp4: AssetType.Video,
  webm: AssetType.Video,
  mov: AssetType.Video,
  // Fonts
  ttf: AssetType.Font,
  otf: AssetType.Font,
  woff: AssetType.Font,
  woff2: AssetType.Font,
  ttc: AssetType.Font,
  // Audio
  mp3: AssetType.Audio,
  wav: AssetType.Audio,
  m4a: AssetType.Audio,
  ogg: AssetType.Audio,
  flac: AssetType.Audio,
  // Documents
  pdf: AssetType.Document,
  csv: AssetType.Document,
  xls: AssetType.Document,
  xlsx: AssetType.Document,
  tsv: AssetType.Document,
  // Code
  js: AssetType.Code,
  html: AssetType.Code,
  css: AssetType.Code,
  xml: AssetType.Code,
  // Subtitles
  srt: AssetType.Subtitles,
  vtt: AssetType.Subtitles,
  // 3D Models
  glb: AssetType.Model3D,
  gltf: AssetType.Model3D,
  obj: AssetType.Model3D,
};

/**
 * Get AssetType from a file's mime type
 * @param mimeType - The mime type string (e.g., 'image/png', 'video/mp4')
 * @returns The corresponding AssetType, or AssetType.Other if not recognized
 */
function getAssetTypeFromMimeType(mimeType: string): AssetType {
  const normalizedMimeType = mimeType.toLowerCase();

  // Check animated images first (GIF can be in both image and video lists)
  if (animatedImageMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    // GIF and APNG are typically animated
    if (normalizedMimeType === CLMimeType.GIF || normalizedMimeType === CLMimeType.APNG) {
      return AssetType.AnimatedImage;
    }
  }

  // Check static images
  if (imageMediaMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Image;
  }

  // Check videos
  if (videoMediaMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Video;
  }

  // Check fonts
  if (fontMediaMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Font;
  }

  // Check audio
  if (audioMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Audio;
  }

  // Check documents
  if (documentMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Document;
  }

  // Check code
  if (codeMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Code;
  }

  // Check subtitles
  if (subtitlesMediaMimeTypes.includes(normalizedMimeType as CLMimeType)) {
    return AssetType.Subtitles;
  }

  // Generic mime type checks for types not in CLMimeType
  if (normalizedMimeType.startsWith('image/')) {
    return AssetType.Image;
  }

  if (normalizedMimeType.startsWith('video/')) {
    return AssetType.Video;
  }

  if (normalizedMimeType.startsWith('audio/')) {
    return AssetType.Audio;
  }

  if (normalizedMimeType.startsWith('font/')) {
    return AssetType.Font;
  }

  if (normalizedMimeType.startsWith('model/')) {
    return AssetType.Model3D;
  }

  return AssetType.Other;
}

/**
 * Get AssetType from a file extension
 * @param extension - The file extension (with or without leading dot, e.g., 'png' or '.png')
 * @returns The corresponding AssetType, or AssetType.Other if not recognized
 */
function getAssetTypeFromExtension(extension: string): AssetType {
  // Remove leading dot if present and normalize to lowercase
  const normalizedExt = extension.replace(/^\./, '').toLowerCase();

  return extensionToAssetTypeMap[normalizedExt] ?? AssetType.Other;
}

/**
 * Get AssetType from a File object
 * Uses both mime type and extension for accurate detection
 * @param file - The File object
 * @returns The corresponding AssetType, or AssetType.Other if not recognized
 */
function getAssetTypeFromFile(file: File): AssetType {
  // First try mime type
  if (file.type) {
    const typeFromMime = getAssetTypeFromMimeType(file.type);

    if (typeFromMime !== AssetType.Other) {
      return typeFromMime;
    }
  }

  // Fall back to extension
  const fileName = file.name;
  const lastDotIndex = fileName.lastIndexOf('.');

  if (lastDotIndex !== -1) {
    const extension = fileName.substring(lastDotIndex + 1);

    return getAssetTypeFromExtension(extension);
  }

  return AssetType.Other;
}

/**
 * Get all file extensions that map to a specific AssetType
 * @param assetType - The asset type to get extensions for
 * @returns Array of extensions (without dots) for the asset type
 */
function getExtensionsByAssetType(assetType: AssetType): string[] {
  return Object.entries(extensionToAssetTypeMap)
    .filter(([, type]) => type === assetType)
    .map(([ext]) => ext);
}

/**
 * Get all known file extensions grouped by AssetType
 * @returns Record mapping AssetType to array of extensions
 */
function getAllExtensionsByAssetType(): Partial<Record<AssetType, string[]>> {
  const result: Partial<Record<AssetType, string[]>> = {};

  for (const [ext, assetType] of Object.entries(extensionToAssetTypeMap)) {
    const extensions = result[assetType];

    if (extensions) {
      extensions.push(ext);
    } else {
      result[assetType] = [ext];
    }
  }

  return result;
}

/**
 * Get all known file extensions as a flat array
 * @returns Array of all known extensions (without dots)
 */
function getAllKnownExtensions(): string[] {
  return Object.keys(extensionToAssetTypeMap);
}

export {
  getAssetTypeFromFile,
  getAssetTypeFromExtension,
  getAssetTypeFromMimeType,
  getExtensionsByAssetType,
  getAllExtensionsByAssetType,
  getAllKnownExtensions,
  extensionToAssetTypeMap,
};

